import { format } from "date-fns";
import { Mail, SendHorizonal, CheckCircle2, XCircle, Clock, AlertTriangle, CalendarClock, CircleDot, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

type ActivityType = "status_change" | "email_sent" | "created" | "rescheduled" | "cancelled" | "note_added" | "reminder_sent" | "payment";

export interface ActivityItem {
  id: string;
  type: ActivityType;
  label: string;
  description?: string;
  timestamp: Date;
  icon?: "mail" | "check" | "cancel" | "clock" | "warning" | "calendar" | "dot" | "send" | "credit-card" | "x-circle";
}

const iconMap = {
  mail: Mail,
  send: SendHorizonal,
  check: CheckCircle2,
  cancel: XCircle,
  clock: Clock,
  warning: AlertTriangle,
  calendar: CalendarClock,
  dot: CircleDot,
  "credit-card": CreditCard,
  "x-circle": XCircle,
};

const iconColorMap: Record<string, string> = {
  mail: "text-primary bg-primary/10",
  send: "text-primary bg-primary/10",
  check: "text-accent bg-accent/10",
  cancel: "text-destructive bg-destructive/10",
  clock: "text-yellow-600 bg-yellow-500/10",
  warning: "text-destructive bg-destructive/10",
  calendar: "text-primary bg-primary/10",
  dot: "text-muted-foreground bg-muted",
  "credit-card": "text-accent bg-accent/10",
  "x-circle": "text-destructive bg-destructive/10",
};

interface ReservationActivityProps {
  activities: ActivityItem[];
}

const ReservationActivity = ({ activities }: ReservationActivityProps) => {
  const { t } = useLanguage();

  return (
    <section className="rounded-xl border border-border bg-card p-6 shadow-card mt-6">
      <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        {t("reservations.activity")}
      </h2>
      <div className="relative">
        <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />

        <div className="space-y-4">
          {activities.map((activity) => {
            const iconKey = activity.icon || "dot";
            const Icon = iconMap[iconKey];
            const colorClass = iconColorMap[iconKey];

            return (
              <div key={activity.id} className="flex gap-3 relative">
                <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full z-10", colorClass)}>
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="flex-1 pt-0.5">
                  <p className="text-sm font-medium text-foreground">{activity.label}</p>
                  {activity.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{activity.description}</p>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {format(activity.timestamp, "MMM d, yyyy 'at' HH:mm")}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ReservationActivity;